import { useEffect, useState } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';

import {
	ONBOARDING_EXPERIENCES,
	TEMPLATES_COLLECTION,
} from '../../constants/onboarding-state/onboarding-constants.ts';
import {
	useGetOnboardingState,
	deserializeState,
	useSetOnboardingState,
} from '../onboardingState/onboardingState.ts';
import { useIsEstablishedUserTraitName } from '../useIsEstablishedUserTraitName.ts';
// eslint-disable-next-line confluence-targeting-usage/no-legacy-tap-personalization-api -- To be migrated to useAtlassianAccountTraits. See ACTIVATION-2527
import { useExperimentalUserPersonalizationQuery } from '../useExperimentalUserPersonalizationQuery.ts';

import type {
	useTemplatesCollectionEligibleQuery$data,
	useTemplatesCollectionEligibleQuery$variables,
} from './__generated__/useTemplatesCollectionEligibleQuery.graphql.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

const useTemplatesCollectionEligibleQuery = graphql`
	query useTemplatesCollectionEligibleQuery {
		confluence_tenantContext {
			licensedProducts {
				licenseStatus
				productKey
			}
		}
		user: confluence_atlassianUser {
			id
			team
			confluence {
				hasPersonalSpace
				space {
					id
					key
				}
			}
		}
	}
`;

export const useTemplatesCollectionEligible = (): {
	isTemplatesCollectionEligible: boolean;
	shouldShowGuidingArrow: boolean;
	teamType: string | null | undefined;
	teamTypeLoading: boolean;
	teamTypeError: ApolloErrorInterop | undefined;
	personalSpaceKey: string | null | undefined;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isUserEligibleForTemplatesCollection, setisUserEligibleForTemplatesCollection] =
		useState(false);
	const { userId } = useSessionData();
	const { setOnboardingState } = useSetOnboardingState();

	const isLicensed = useIsLicensed();
	const isEstablishedUserTraitName = useIsEstablishedUserTraitName();
	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	const skipConditionsForQueries = !isLicensed || !fg('cc_onboarding_experience');

	const {
		data: userPersonalizationQueryData,
		loading: userPersonalizationQueryLoading,
		error: userPersonalizationQueryError,
	} = useExperimentalUserPersonalizationQuery({
		variables: {
			userId: userId ?? '',
		},
		skip: skipConditionsForQueries,
	});
	const attributes =
		userPersonalizationQueryData?.experimentalUserPersonalization?.attributes ?? [];

	const isEstablishedUser =
		attributes.find((attr: any) => attr?.name === isEstablishedUserTraitName)?.value === 'true';

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		Object.values({
			...TEMPLATES_COLLECTION,
			...ONBOARDING_EXPERIENCES,
		}),
		skipConditionsForQueries,
	);
	// check if user is a founding admin or/and if the user is tagged already
	const { isFoundingAdmin, isTaggedForTemplatesCollectionExperiment, isTemplatesSectionDismissed } =
		deserializeState(onboardingStateData);

	const {
		data: productsTeamAndSpaceQueryData,
		loading: productsTeamAndSpaceQueryLoading,
		error: productsTeamAndSpaceQueryError,
	} = useQueryInterop<
		useTemplatesCollectionEligibleQuery$data,
		useTemplatesCollectionEligibleQuery$variables
	>(useTemplatesCollectionEligibleQuery, {
		skip: skipConditionsForQueries || isTemplatesSectionDismissed,
	});

	const hasPersonalSpace = productsTeamAndSpaceQueryData?.user?.confluence?.hasPersonalSpace;

	const isXFlowUser =
		productsTeamAndSpaceQueryData?.confluence_tenantContext?.licensedProducts.some(
			(product: { productKey: string; licenseStatus: string }) =>
				product.productKey.toLowerCase().includes('jira') && product.licenseStatus === 'ACTIVE',
		);

	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	const isUserEligible = isLicensed && fg('cc_onboarding_experience');

	const isUserEligibleForTemplatesCollectionTagging =
		isUserEligible && isFoundingAdmin && isXFlowUser;

	useEffect(() => {
		if (
			// queries have finished loading
			!userPersonalizationQueryError &&
			!userPersonalizationQueryLoading &&
			!onboardingStateLoading &&
			!onboardingStateError &&
			!productsTeamAndSpaceQueryError &&
			!productsTeamAndSpaceQueryLoading &&
			// conditions to meet to be eligible
			!isEstablishedUser &&
			isUserEligibleForTemplatesCollectionTagging &&
			!isTaggedForTemplatesCollectionExperiment
		) {
			// newly tagged user for the experiment
			setisUserEligibleForTemplatesCollection(true);

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'exposed',
					actionSubject: 'feature',
					attributes: {
						name: 'templatesCollection',
						hasPersonalSpace,
					},
				},
			}).fire();

			void setOnboardingState({
				key: TEMPLATES_COLLECTION.EXPERIMENT_ELIGIBLE,
				value: String(true),
			});
		}
	}, [
		userPersonalizationQueryError,
		userPersonalizationQueryLoading,
		onboardingStateError,
		onboardingStateLoading,
		productsTeamAndSpaceQueryError,
		productsTeamAndSpaceQueryLoading,
		isEstablishedUser,
		isUserEligibleForTemplatesCollectionTagging,
		isTaggedForTemplatesCollectionExperiment,
		setisUserEligibleForTemplatesCollection,
		setOnboardingState,
		createAnalyticsEvent,
		hasPersonalSpace,
	]);

	const isUserPreviouslyTaggedAndEnrolled = Boolean(isTaggedForTemplatesCollectionExperiment);

	const isTemplatesCollectionEligible =
		!isTemplatesSectionDismissed &&
		Boolean(hasPersonalSpace) &&
		(isUserEligibleForTemplatesCollection || isUserPreviouslyTaggedAndEnrolled);

	return {
		isTemplatesCollectionEligible,
		shouldShowGuidingArrow: isTemplatesCollectionEligible && !isEstablishedUser,
		teamType: productsTeamAndSpaceQueryData?.user?.team,
		teamTypeLoading: productsTeamAndSpaceQueryLoading,
		teamTypeError: productsTeamAndSpaceQueryError,
		personalSpaceKey: productsTeamAndSpaceQueryData?.user?.confluence?.space?.key,
	};
};
